import React, { useEffect, useState } from 'react';
import { WithStyles } from '@material-ui/core/styles';
import { useMutation, useQuery } from '@apollo/client';
import {
  Grid,
  TextField,
  Button,
  withStyles,
  makeStyles,
  Paper,
  IconButton,
  Typography,
  Box,
  Chip,
} from '@material-ui/core';
import { Add as AddIcon, Delete as DeleteIcon } from '@material-ui/icons';
import CustomSortableTable, {
  SortableTableHeader,
  SortableTableRow,
} from '../../../../../CustomSortableTable';
import {
  GET_AVAILABILITY_ITEMS,
  INSERT_AVAILABILITY_WINDOW,
  DELETE_AVAILABILITY_WINDOW,
  DELETE_AVAILABILITY_WINDOWS_FOR_ITEM,
} from './queries';
import styles from './styles';
import { useSnackbar } from 'notistack';

interface Props extends WithStyles<typeof styles> {
  location_id: string;
  organisation_id: string;
}

interface AvailabilityWindow {
  id: string;
  dish_id: string | null;
  drink_id: string | null;
  category_id: string | null;
  day_of_week: string;
  open_time: string;
  close_time: string;
}

interface ItemDetail {
  id: string;
  name: string;
  order_index?: number;
  category_assignments: {
    category_id: string;
    category: {
      id: string;
      name: string;
      order_index?: number;
    };
  }[];
}

interface StockItem {
  id: string;
  dish_id?: string;
  drink_id?: string;
  dish_item?: ItemDetail;
  drink_item?: ItemDetail;
}

const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DEFAULT_NEW_WINDOW = { day_of_week: 'Monday', open_time: '', close_time: '' };

type NewWindowField = keyof typeof DEFAULT_NEW_WINDOW;

const getItemDetail = (item: StockItem): ItemDetail | undefined =>
  item.dish_item || item.drink_item;

const getCategoryOrderIndex = (item: StockItem): number =>
  getItemDetail(item)?.category_assignments?.[0]?.category?.order_index ?? 0;

const useStyles = makeStyles({
  button: {
    margin: '0 4px',
  },
  tableHeader: {
    fontWeight: 'bold',
    fontSize: '1rem',
  },
  timeField: {
    width: '120px',
  },
  windowRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  },
  dayChip: {
    minWidth: '90px',
  },
});

const StepAvailability = ({ classes, location_id, organisation_id }: Props): React.ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const customStyles = useStyles();

  const { data, loading, error, refetch } = useQuery(GET_AVAILABILITY_ITEMS, {
    variables: { locationId: location_id },
    fetchPolicy: 'network-only',
  });

  const [insertWindow] = useMutation(INSERT_AVAILABILITY_WINDOW);
  const [deleteWindow] = useMutation(DELETE_AVAILABILITY_WINDOW);
  const [deleteWindowsForItem] = useMutation(DELETE_AVAILABILITY_WINDOWS_FOR_ITEM);

  const [items, setItems] = useState<StockItem[]>([]);
  const [availabilityWindows, setAvailabilityWindows] = useState<AvailabilityWindow[]>([]);

  // State for new window being added per item
  const [newWindows, setNewWindows] = useState<{
    [key: string]: { day_of_week: string; open_time: string; close_time: string };
  }>({});

  useEffect(() => {
    if (data) {
      if (data.menu_location_stock) {
        const validItems = data.menu_location_stock.filter((item: StockItem) => {
          const detail = getItemDetail(item);
          return detail?.name && (detail?.category_assignments?.length ?? 0) > 0;
        });
        setItems(validItems);
      }
      if (data.item_availability_windows) {
        setAvailabilityWindows(data.item_availability_windows);
      }
    }
  }, [data]);

  const getItemKey = (item: StockItem) => {
    return item.dish_id ? `dish_${item.dish_id}` : `drink_${item.drink_id}`;
  };

  const getWindowsForItem = (item: StockItem): AvailabilityWindow[] => {
    if (item.dish_id) {
      return availabilityWindows.filter((w) => w.dish_id === item.dish_id);
    }
    return availabilityWindows.filter((w) => w.drink_id === item.drink_id);
  };

  const handleAddWindow = async (item: StockItem) => {
    const key = getItemKey(item);
    const newWindow = newWindows[key];
    if (!newWindow || !newWindow.day_of_week || !newWindow.open_time || !newWindow.close_time) {
      enqueueSnackbar('Please select a day and set both open and close times', { variant: 'warning' });
      return;
    }

    try {
      await insertWindow({
        variables: {
          location_id,
          organisation_id,
          dish_id: item.dish_id || null,
          drink_id: item.drink_id || null,
          category_id: null,
          day_of_week: newWindow.day_of_week,
          open_time: newWindow.open_time,
          close_time: newWindow.close_time,
        },
      });

      // Clear the new window form for this item
      setNewWindows((prev) => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });

      refetch();
      enqueueSnackbar('Availability window added', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar('Failed to add availability window', { variant: 'error' });
    }
  };

  const handleDeleteWindow = async (windowId: string) => {
    try {
      await deleteWindow({ variables: { id: windowId } });
      refetch();
      enqueueSnackbar('Availability window removed', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar('Failed to remove availability window', { variant: 'error' });
    }
  };

  const handleClearAllWindows = async (item: StockItem) => {
    try {
      await deleteWindowsForItem({
        variables: {
          location_id,
          dish_id: item.dish_id || null,
          drink_id: item.drink_id || null,
          category_id: null,
        },
      });
      refetch();
      enqueueSnackbar('All availability windows cleared (item is now always available)', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar('Failed to clear availability windows', { variant: 'error' });
    }
  };

  const updateNewWindow = (itemKey: string, field: NewWindowField, value: string) => {
    setNewWindows((prev) => ({
      ...prev,
      [itemKey]: {
        ...(prev[itemKey] || DEFAULT_NEW_WINDOW),
        [field]: value,
      },
    }));
  };

  // Group items by category
  const groupItemsByCategory = (stockItems: StockItem[]) => {
    const groupedItems: { [key: string]: StockItem[] } = {};

    stockItems.forEach((item) => {
      const categoryName = getItemDetail(item)?.category_assignments?.[0]?.category?.name;
      if (categoryName) {
        if (!groupedItems[categoryName]) {
          groupedItems[categoryName] = [];
        }
        groupedItems[categoryName].push(item);
      }
    });

    Object.keys(groupedItems).forEach((category) => {
      groupedItems[category].sort((a, b) => {
        const aOrder = getItemDetail(a)?.order_index ?? 0;
        const bOrder = getItemDetail(b)?.order_index ?? 0;
        if (aOrder !== bOrder) return aOrder - bOrder;
        const aName = getItemDetail(a)?.name || '';
        const bName = getItemDetail(b)?.name || '';
        return aName.localeCompare(bName);
      });
    });

    return groupedItems;
  };

  if (error) return <p>Error: {error.message}</p>;

  const groupedItems = groupItemsByCategory(items);

  const headers: SortableTableHeader[] = [{ key: 'item_name', label: 'Item Name', align: 'left' }];

  const collapsibleHeaders: SortableTableHeader[] = [
    { key: 'item_name', label: 'Item Name', align: 'left' },
    { key: 'windows', label: 'Availability Windows', align: 'left' },
    { key: 'actions', label: 'Actions', align: 'center' },
  ];

  const sortedCategoryKeys = Object.keys(groupedItems).sort((a, b) => {
    const aOrder = getCategoryOrderIndex(groupedItems[a][0]);
    const bOrder = getCategoryOrderIndex(groupedItems[b][0]);
    if (aOrder !== bOrder) return aOrder - bOrder;
    return a.localeCompare(b);
  });

  const formatTime = (time: string) => {
    // Convert "HH:MM:SS+00" to "HH:MM"
    return time ? time.slice(0, 5) : '';
  };

  const rows: SortableTableRow[] = sortedCategoryKeys.map((category) => ({
    key: category,
    columns: [
      {
        key: 'category_name',
        label: category,
        colSpan: headers.length,
        className: customStyles.tableHeader,
      },
    ],
    collapsible: [
      {
        title: category,
        headers: collapsibleHeaders,
        counter: groupedItems[category].length,
        rows: groupedItems[category].map((item) => {
          const itemKey = getItemKey(item);
          const itemWindows = getWindowsForItem(item);
          const newWindow = newWindows[itemKey] || DEFAULT_NEW_WINDOW;

          return {
            key: item.id,
            columns: [
              {
                key: 'item_name',
                label: getItemDetail(item)?.name || '',
              },
              {
                key: 'windows',
                label: itemWindows.length > 0 ? `${itemWindows.length} window(s)` : 'Always available',
                component: (
                  <Box>
                    {/* Existing windows */}
                    {itemWindows
                      .sort((a, b) => {
                        const dayOrder = daysOfWeek.indexOf(a.day_of_week) - daysOfWeek.indexOf(b.day_of_week);
                        if (dayOrder !== 0) return dayOrder;
                        return a.open_time.localeCompare(b.open_time);
                      })
                      .map((w) => (
                        <Box key={w.id} className={customStyles.windowRow}>
                          <Chip label={w.day_of_week} size="small" className={customStyles.dayChip} />
                          <Typography variant="body2">
                            {formatTime(w.open_time)} - {formatTime(w.close_time)}
                          </Typography>
                          <IconButton size="small" onClick={() => handleDeleteWindow(w.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}

                    {/* Add new window form */}
                    <Box className={customStyles.windowRow} style={{ marginTop: '8px' }}>
                      <TextField
                        select
                        SelectProps={{ native: true }}
                        value={newWindow.day_of_week}
                        onChange={(e) => updateNewWindow(itemKey, 'day_of_week', e.target.value)}
                        size="small"
                        variant="outlined"
                        style={{ width: '130px' }}>
                        {daysOfWeek.map((day) => (
                          <option key={day} value={day}>
                            {day}
                          </option>
                        ))}
                      </TextField>
                      <TextField
                        type="time"
                        value={newWindow.open_time}
                        onChange={(e) => updateNewWindow(itemKey, 'open_time', e.target.value)}
                        className={customStyles.timeField}
                        size="small"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                      />
                      <Typography variant="body2">to</Typography>
                      <TextField
                        type="time"
                        value={newWindow.close_time}
                        onChange={(e) => updateNewWindow(itemKey, 'close_time', e.target.value)}
                        className={customStyles.timeField}
                        size="small"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                      />
                      <IconButton size="small" color="primary" onClick={() => handleAddWindow(item)}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {itemWindows.length === 0 && (
                      <Typography variant="caption" color="textSecondary" style={{ marginTop: '4px' }}>
                        No windows set - item is always available
                      </Typography>
                    )}
                  </Box>
                ),
              },
              {
                key: 'actions',
                label: '',
                component: itemWindows.length > 0 ? (
                  <Button
                    onClick={() => handleClearAllWindows(item)}
                    variant="outlined"
                    color="secondary"
                    size="small"
                    className={customStyles.button}>
                    Clear All
                  </Button>
                ) : undefined,
              },
            ],
          };
        }),
      },
    ],
  }));

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <CustomSortableTable
            noSorting
            title="Item Availability Windows"
            searchable={['dish_item.name', 'drink_item.name']}
            counter={rows.length}
            headers={headers}
            rows={rows}
            loading={loading}
          />
        </Paper>
      </Grid>
    </Grid>
  );
};

export default withStyles(styles)(StepAvailability);
